// packages/web-ui/src/components/SessionList/SessionList.tsx
//
// W2-S8 — Sessions list rendered inside the ContextPanel Mode A (Chat).
//
// Story-narrowed scope (per parent prompt):
//   - List sessions from the `sessions` slice (`refreshSessions` on mount).
//   - `[+ New conversation]` CTA → `createSession()` → activate via callback.
//   - Click row → activate.
//   - Per-row × → open `<DeleteConfirmDialog>` → hard delete via slice.
//   - Search input — case-insensitive filter over title + sessionId.
//
// OUT of scope here (deferred to a follow-up story):
//   - Pin / Unpin / Archive / Rename / Export.
//   - Sectioned list (Pinned / Recent / Archived).
//   - Workspace-hash localStorage prefs.
//   - Onboarding "Recent top-5" Empty Welcome surface (W2-S10).
//
// The slice instance is passed in via prop so the host component
// (ChatMode) owns its lifecycle and tests can inject a mocked store.

import type { JSX } from 'react';
import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useStore } from 'zustand';
import type { StoreApi } from 'zustand';
import type { SessionSummary } from '@tessa-lean/base/api-schemas/index.js';
import {
  selectFilteredSessions,
  type SessionsSlice,
} from '../../state/sessions.js';
import { SessionRow } from './SessionRow.js';
import { DeleteConfirmDialog } from './DeleteConfirmDialog.js';
import { NEW_SESSION_EVENT } from '../../hooks/useKeymap.js';

export interface SessionListProps {
  /** Vanilla zustand store providing the sessions slice. */
  store: StoreApi<SessionsSlice>;
  /** Currently-active sessionId (from the navigation/router). */
  activeSessionId?: string | null;
  /**
   * Called after the slice creates a new session. The host activates the
   * session in the tab area / hash router.
   */
  onActivate: (sessionId: string) => void;
  /**
   * Called after one or more sessions are hard-deleted (single row delete or
   * "Delete all"), with the deleted sessionIds. The host closes the matching
   * chat tabs — a deleted session must not leave an orphaned tab behind.
   */
  onSessionsDeleted?: (sessionIds: string[]) => void;
  /** Optional test id override. */
  testId?: string;
}

export function SessionList({
  store,
  activeSessionId,
  onActivate,
  onSessionsDeleted,
  testId = 'session-list',
}: SessionListProps): JSX.Element {
  const state = useStore(store);
  const [search, setSearch] = useState('');
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);

  // We only want to bootstrap once on mount; subsequent refreshes are explicit
  // (Retry button, future invalidation events). Read the latest store state
  // directly via `store.getState()` so the effect has no reactive deps.
  useEffect(() => {
    const snapshot = store.getState();
    if (snapshot.status === 'idle') {
      void snapshot.refreshSessions();
    }
  }, [store]);

  const filtered = useMemo<SessionSummary[]>(
    () => selectFilteredSessions(state, search),
    [state, search],
  );

  const pendingTitle = useMemo<string>(() => {
    if (state.pendingDelete === null) return '';
    const target = state.sessions.find(
      (s) => s.sessionId === state.pendingDelete?.sessionId,
    );
    return target?.title && target.title.trim().length > 0 ? target.title : 'Untitled';
  }, [state.pendingDelete, state.sessions]);

  const handleCreate = useCallback(async (): Promise<void> => {
    const id = await store.getState().createSession();
    if (id !== null) onActivate(id);
  }, [store, onActivate]);

  // Polish §8 — Ctrl+N global keybinding. `useGlobalKeymap` dispatches the
  // `tessa:newSession` event; the SessionList that is currently mounted
  // (Chat context-panel mode) consumes it and routes to the same create
  // flow the `+ New` button uses. When no SessionList is mounted (Settings
  // / KB / etc. destinations), no listener is registered → Ctrl+N no-ops.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    function handleNewSession(): void {
      void handleCreate();
    }
    window.addEventListener(NEW_SESSION_EVENT, handleNewSession);
    return (): void => {
      window.removeEventListener(NEW_SESSION_EVENT, handleNewSession);
    };
  }, [handleCreate]);

  async function handleConfirmDelete(): Promise<void> {
    const id = state.pendingDelete?.sessionId;
    const ok = await state.confirmDelete();
    if (ok && id !== undefined) {
      // Close the deleted session's tab so it doesn't linger as an orphan.
      // The host also reconciles the active session if it was the one removed.
      onSessionsDeleted?.([id]);
    }
  }

  async function handleConfirmDeleteAll(): Promise<void> {
    // Snapshot the ids before `deleteAllSessions()` optimistically clears the
    // list, so the host can close every related chat tab.
    const ids = store.getState().sessions.map((s) => s.sessionId);
    const result = await state.deleteAllSessions();
    if (result !== null) {
      setDeleteAllOpen(false);
      onSessionsDeleted?.(ids);
    }
  }

  return (
    <div
      data-testid={testId}
      role="navigation"
      aria-label="Chat sessions"
      className="flex h-full flex-col bg-cream-100"
    >
      <div className="flex h-8 items-center justify-between border-b border-taupe-300 px-2 text-sm font-semibold">
        <span>Chat sessions</span>
        <div className="flex items-center gap-1">
          {state.sessions.length > 0 ? (
            <button
              type="button"
              data-testid={`${testId}-delete-all`}
              aria-label="Delete all chat sessions"
              onClick={() => {
                setDeleteAllOpen(true);
              }}
              disabled={state.deleteAllInFlight}
              aria-busy={state.deleteAllInFlight ? 'true' : undefined}
              className="text-audi-red rounded px-2 py-0.5 text-xs hover:bg-cream-200 disabled:opacity-60"
            >
              {state.deleteAllInFlight ? 'Deleting…' : 'Delete all'}
            </button>
          ) : null}
          <button
            type="button"
            data-testid={`${testId}-new`}
            aria-label="Create new chat session"
            onClick={() => {
              void handleCreate();
            }}
            disabled={state.createInFlight}
            aria-busy={state.createInFlight ? 'true' : undefined}
            className="rounded px-2 py-0.5 text-xs hover:bg-cream-200 disabled:opacity-60"
          >
            {state.createInFlight ? 'Creating…' : '+ New'}
          </button>
        </div>
      </div>

      <div className="border-b border-taupe-300 bg-cream-100 p-1">
        <input
          type="text"
          role="searchbox"
          data-testid={`${testId}-search`}
          aria-label="Search sessions"
          placeholder="Search sessions…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.stopPropagation();
              setSearch('');
            }
          }}
          className="h-7 w-full rounded border border-taupe-300 bg-cream-50 px-2 text-xs outline-none focus:border-taupe-700"
        />
      </div>

      <div
        className="flex-1 overflow-y-auto"
        data-testid={`${testId}-body`}
        data-status={state.status}
      >
        {state.status === 'loading' && state.sessions.length === 0 ? (
          <div
            data-testid={`${testId}-loading`}
            className="px-3 py-2 text-xs italic text-taupe-700"
          >
            Loading sessions…
          </div>
        ) : null}

        {state.status === 'error' ? (
          <div
            role="alert"
            data-testid={`${testId}-error`}
            className="bg-accent-red/10 text-accent-red px-3 py-2 text-xs flex items-center justify-between gap-2"
          >
            <span>{state.lastError?.message ?? 'Failed to load sessions.'}</span>
            <button
              type="button"
              data-testid={`${testId}-retry`}
              className="rounded border border-accent-red bg-cream-100 px-2 py-0.5 text-xs"
              onClick={() => {
                void state.refreshSessions();
              }}
            >
              Retry
            </button>
          </div>
        ) : null}

        {filtered.length === 0 && state.status !== 'loading' && state.status !== 'error' ? (
          <div
            data-testid={`${testId}-empty`}
            className="px-3 py-2 text-xs italic text-taupe-700"
          >
            {search.trim().length > 0
              ? `No sessions match "${search.trim()}".`
              : 'No conversations yet. Click + New to start one.'}
          </div>
        ) : null}

        {filtered.length > 0 ? (
          <ul role="list" className="flex flex-col" data-testid={`${testId}-list`}>
            {filtered.map((session) => (
              <SessionRow
                key={session.sessionId}
                session={session}
                isActive={session.sessionId === activeSessionId}
                onActivate={onActivate}
                onRequestDelete={state.requestDelete}
              />
            ))}
          </ul>
        ) : null}
      </div>

      <DeleteConfirmDialog
        open={state.pendingDelete !== null}
        sessionTitle={pendingTitle}
        isDeleting={state.pendingDelete?.isDeleting ?? false}
        onCancel={state.cancelDelete}
        onConfirm={() => {
          void handleConfirmDelete();
        }}
      />

      <DeleteAllSessionsConfirmDialog
        open={deleteAllOpen}
        sessionCount={state.sessions.length}
        isDeleting={state.deleteAllInFlight}
        onCancel={() => {
          setDeleteAllOpen(false);
        }}
        onConfirm={() => {
          void handleConfirmDeleteAll();
        }}
      />
    </div>
  );
}

interface DeleteAllSessionsConfirmDialogProps {
  open: boolean;
  sessionCount: number;
  isDeleting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

function DeleteAllSessionsConfirmDialog({
  open,
  sessionCount,
  isDeleting,
  onCancel,
  onConfirm,
}: DeleteAllSessionsConfirmDialogProps): JSX.Element | null {
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open) cancelRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onCancel]);

  if (!open) return null;

  const heading =
    sessionCount === 1
      ? 'Permanently delete the saved conversation?'
      : `Permanently delete all ${String(sessionCount)} saved conversations?`;

  const dialog = (
    <div
      data-testid="sessions-delete-all-dialog"
      className="fixed inset-0 flex items-center justify-center bg-black/60"
      style={{ zIndex: 100 }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="sessions-delete-all-heading"
        aria-describedby="sessions-delete-all-body"
        className="bg-cream-50 border-taupe-500 mx-4 w-full max-w-md rounded-none border p-5"
      >
        <h2
          id="sessions-delete-all-heading"
          data-testid="sessions-delete-all-dialog-heading"
          className="text-fg text-base font-extrabold tracking-[-0.01em]"
        >
          {heading}
        </h2>
        <p
          id="sessions-delete-all-body"
          data-testid="sessions-delete-all-dialog-body"
          className="text-taupe-700 mt-2 text-sm"
        >
          This cannot be undone. Every saved conversation file will be removed from disk.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <button
            ref={cancelRef}
            type="button"
            data-testid="sessions-delete-all-dialog-cancel"
            className="border-taupe-500 hover:border-taupe-900 hover:bg-cream-100 text-fg rounded-none border px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide"
            onClick={onCancel}
            disabled={isDeleting}
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="sessions-delete-all-dialog-confirm"
            className="bg-audi-red hover:bg-audi-red-hover rounded-none px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-white disabled:opacity-60"
            onClick={onConfirm}
            disabled={isDeleting}
            aria-busy={isDeleting ? 'true' : undefined}
          >
            {isDeleting ? 'Deleting…' : 'Delete all'}
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return dialog;
  return createPortal(dialog, document.body);
}
